import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useWelcome } from '../context/WelcomeContext';
import { useLanguage } from '../context/LanguageContext';
import { usePWA } from '../context/PWAContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useAEAE, UI_TONES, MESSAGE_VARIANTS, ANIMATION_DURATIONS } from '../hooks/useAEAE';
import { 
  Sparkles, 
  Code, 
  Image, 
  MessageSquare, 
  Download,
  ChevronRight,
  X,
  Smartphone,
  Monitor,
  Zap,
  Heart,
  ArrowRight,
  Share
} from 'lucide-react';

// Platform-specific tips with emotional variants
const platformTips = {
  android: {
    es: {
      standard: [
        { icon: Download, text: 'Instala la app desde el menu del navegador para acceso rapido' },
        { icon: Share, text: 'Comparte tus creaciones directamente desde la app' },
        { icon: Zap, text: 'Desliza entre Chat y Trabajo para navegar' }
      ],
      calm: [
        { icon: Download, text: 'Cuando quieras, puedes instalar la app para acceder mas facil' },
        { icon: Share, text: 'Comparte lo que crees cuando te sientas listo' },
        { icon: Zap, text: 'Navega con calma deslizando entre Chat y Trabajo' }
      ],
      energetic: [
        { icon: Download, text: 'Instala la app y ten DamyxIA siempre a un toque' },
        { icon: Share, text: 'Comparte tus creaciones al instante con tus amigos' },
        { icon: Zap, text: 'Desliza rapido entre Chat y Trabajo para ser mas productivo' }
      ],
      empathetic: [
        { icon: Download, text: 'Si lo deseas, puedes instalar la app para mayor comodidad' },
        { icon: Share, text: 'Comparte tus proyectos cuando estes satisfecho con ellos' },
        { icon: Zap, text: 'Toma tu tiempo para explorar Chat y Trabajo' }
      ]
    },
    en: {
      standard: [
        { icon: Download, text: 'Install the app from browser menu for quick access' },
        { icon: Share, text: 'Share your creations directly from the app' },
        { icon: Zap, text: 'Swipe between Chat and Work to navigate' }
      ],
      calm: [
        { icon: Download, text: 'When ready, you can install the app for easier access' },
        { icon: Share, text: 'Share what you create when you feel ready' },
        { icon: Zap, text: 'Navigate calmly by swiping between Chat and Work' }
      ],
      energetic: [
        { icon: Download, text: 'Install the app and have DamyxIA always at your fingertips' },
        { icon: Share, text: 'Share your creations instantly with friends' },
        { icon: Zap, text: 'Swipe fast between Chat and Work to be more productive' }
      ],
      empathetic: [
        { icon: Download, text: 'If you wish, you can install the app for convenience' },
        { icon: Share, text: 'Share your projects when you are satisfied with them' },
        { icon: Zap, text: 'Take your time exploring Chat and Work' }
      ]
    }
  },
  ios: {
    es: {
      standard: [
        { icon: Download, text: 'Toca "Compartir" y luego "Anadir a inicio" para instalar' },
        { icon: Share, text: 'Comparte tus proyectos con un solo toque' },
        { icon: Zap, text: 'Desliza entre Chat y Trabajo para navegar' }
      ],
      calm: [
        { icon: Download, text: 'Cuando quieras instalar: Compartir > Anadir a inicio' },
        { icon: Share, text: 'Comparte tus proyectos a tu ritmo' },
        { icon: Zap, text: 'Explora con calma deslizando entre secciones' }
      ],
      energetic: [
        { icon: Download, text: 'Instala rapido: Compartir > Anadir a inicio' },
        { icon: Share, text: 'Comparte al instante con un toque' },
        { icon: Zap, text: 'Navega agil entre Chat y Trabajo' }
      ],
      empathetic: [
        { icon: Download, text: 'Si deseas instalar: Compartir > Anadir a inicio' },
        { icon: Share, text: 'Comparte cuando te sientas comodo' },
        { icon: Zap, text: 'Toma el tiempo que necesites para explorar' }
      ]
    },
    en: {
      standard: [
        { icon: Download, text: 'Tap "Share" then "Add to Home Screen" to install' },
        { icon: Share, text: 'Share your projects with one tap' },
        { icon: Zap, text: 'Swipe between Chat and Work to navigate' }
      ],
      calm: [
        { icon: Download, text: 'When ready to install: Share > Add to Home Screen' },
        { icon: Share, text: 'Share your projects at your own pace' },
        { icon: Zap, text: 'Explore calmly by swiping between sections' }
      ],
      energetic: [
        { icon: Download, text: 'Quick install: Share > Add to Home Screen' },
        { icon: Share, text: 'Share instantly with one tap' },
        { icon: Zap, text: 'Navigate swiftly between Chat and Work' }
      ],
      empathetic: [
        { icon: Download, text: 'If you wish to install: Share > Add to Home Screen' },
        { icon: Share, text: 'Share when you feel comfortable' },
        { icon: Zap, text: 'Take the time you need to explore' }
      ]
    }
  },
  desktop: {
    es: {
      standard: [
        { icon: Monitor, text: 'Usa los paneles redimensionables para tu flujo de trabajo' },
        { icon: Code, text: 'El codigo generado aparece en el panel derecho' },
        { icon: Zap, text: 'Atajos: Ctrl+Enter para enviar mensaje' }
      ],
      calm: [
        { icon: Monitor, text: 'Ajusta los paneles a tu gusto, sin prisa' },
        { icon: Code, text: 'Tu codigo aparecera en el panel derecho cuando lo generes' },
        { icon: Zap, text: 'Tip: Ctrl+Enter envia mensajes cuando estes listo' }
      ],
      energetic: [
        { icon: Monitor, text: 'Optimiza tu espacio con paneles redimensionables' },
        { icon: Code, text: 'Codigo al instante en el panel derecho' },
        { icon: Zap, text: 'Productividad: Ctrl+Enter para enviar rapido' }
      ],
      empathetic: [
        { icon: Monitor, text: 'Personaliza los paneles como te sea mas comodo' },
        { icon: Code, text: 'El codigo se mostrara en el panel derecho' },
        { icon: Zap, text: 'Cuando quieras: Ctrl+Enter envia mensajes' }
      ]
    },
    en: {
      standard: [
        { icon: Monitor, text: 'Use resizable panels for your workflow' },
        { icon: Code, text: 'Generated code appears in the right panel' },
        { icon: Zap, text: 'Shortcuts: Ctrl+Enter to send message' }
      ],
      calm: [
        { icon: Monitor, text: 'Adjust panels at your own pace' },
        { icon: Code, text: 'Your code will appear in the right panel when generated' },
        { icon: Zap, text: 'Tip: Ctrl+Enter sends messages when ready' }
      ],
      energetic: [
        { icon: Monitor, text: 'Optimize your space with resizable panels' },
        { icon: Code, text: 'Instant code in the right panel' },
        { icon: Zap, text: 'Productivity: Ctrl+Enter for quick send' }
      ],
      empathetic: [
        { icon: Monitor, text: 'Customize panels as you feel comfortable' },
        { icon: Code, text: 'Code will be shown in the right panel' },
        { icon: Zap, text: 'When ready: Ctrl+Enter sends messages' }
      ]
    }
  }
};

// Capabilities with emotional variants
const capabilities = {
  es: {
    standard: [
      { icon: Code, title: 'Genera codigo', desc: 'En cualquier lenguaje' },
      { icon: Image, title: 'Crea imagenes', desc: 'Con inteligencia artificial' },
      { icon: MessageSquare, title: 'Conversaciones', desc: 'Memoria contextual inteligente' }
    ],
    calm: [
      { icon: Code, title: 'Genera codigo', desc: 'A tu ritmo, en cualquier lenguaje' },
      { icon: Image, title: 'Crea imagenes', desc: 'Con IA, cuando lo necesites' },
      { icon: MessageSquare, title: 'Conversaciones', desc: 'Te recuerdo para ayudarte mejor' }
    ],
    energetic: [
      { icon: Code, title: 'Codigo al instante', desc: 'Python, JavaScript, cualquiera' },
      { icon: Image, title: 'Imagenes increibles', desc: 'IA de ultima generacion' },
      { icon: MessageSquare, title: 'Charlas inteligentes', desc: 'Memoria que potencia cada respuesta' }
    ],
    empathetic: [
      { icon: Code, title: 'Codigo para ti', desc: 'En el lenguaje que prefieras' },
      { icon: Image, title: 'Tus imagenes', desc: 'Creadas con cuidado por IA' },
      { icon: MessageSquare, title: 'Conversaciones', desc: 'Te escucho y recuerdo' }
    ]
  },
  en: {
    standard: [
      { icon: Code, title: 'Generate code', desc: 'In any language' },
      { icon: Image, title: 'Create images', desc: 'With artificial intelligence' },
      { icon: MessageSquare, title: 'Conversations', desc: 'Smart contextual memory' }
    ],
    calm: [
      { icon: Code, title: 'Generate code', desc: 'At your pace, any language' },
      { icon: Image, title: 'Create images', desc: 'With AI, when you need' },
      { icon: MessageSquare, title: 'Conversations', desc: 'I remember to help you better' }
    ],
    energetic: [
      { icon: Code, title: 'Instant code', desc: 'Python, JavaScript, anything' },
      { icon: Image, title: 'Amazing images', desc: 'Cutting-edge AI' },
      { icon: MessageSquare, title: 'Smart chats', desc: 'Memory that powers every response' }
    ],
    empathetic: [
      { icon: Code, title: 'Code for you', desc: 'In your preferred language' },
      { icon: Image, title: 'Your images', desc: 'Carefully crafted by AI' },
      { icon: MessageSquare, title: 'Conversations', desc: 'I listen and remember' }
    ]
  }
};

// Emotional tone messages with variants
const toneMessages = {
  standard: {
    es: {
      greeting: 'Bienvenido a DamyxIA!',
      subtitle: 'Tu asistente de inteligencia artificial personal',
      description: 'Estoy aqui para ayudarte a crear, programar y dar vida a tus ideas.',
      cta: 'Comenzar'
    },
    en: {
      greeting: 'Welcome to DamyxIA!',
      subtitle: 'Your personal AI assistant',
      description: "I'm here to help you create, code, and bring your ideas to life.",
      cta: 'Get Started'
    }
  },
  calm: {
    es: {
      greeting: 'Hola, bienvenido',
      subtitle: 'Tomate tu tiempo para explorar',
      description: 'No te preocupes si es tu primera vez. Estoy aqui para guiarte paso a paso, sin prisa.',
      cta: 'Explorar tranquilo'
    },
    en: {
      greeting: 'Hello, welcome',
      subtitle: 'Take your time to explore',
      description: "Don't worry if it's your first time. I'm here to guide you step by step, no rush.",
      cta: 'Explore calmly'
    }
  },
  energetic: {
    es: {
      greeting: 'Hola! Que emocion tenerte aqui!',
      subtitle: 'Vamos a crear algo increible',
      description: 'Tienes un mundo de posibilidades! Codigo, imagenes, diagramas... empecemos!',
      cta: 'Vamos!'
    },
    en: {
      greeting: "Hi! So excited to have you here!",
      subtitle: "Let's create something amazing",
      description: "You have a world of possibilities! Code, images, diagrams... let's go!",
      cta: "Let's go!"
    }
  },
  empathetic: {
    es: {
      greeting: 'Hola, me alegra que estes aqui',
      subtitle: 'Estoy para ayudarte',
      description: 'Sea cual sea tu proyecto o duda, estoy aqui para acompanarte. Sin prisas, a tu ritmo.',
      cta: 'Empezar juntos'
    },
    en: {
      greeting: "Hello, I'm glad you're here",
      subtitle: "I'm here to help you",
      description: "Whatever your project or question, I'm here to accompany you. No rush, at your pace.",
      cta: 'Start together'
    }
  }
};

const WelcomeFlow = () => {
  const { showWelcome, completeWelcome, setTone: setWelcomeTone } = useWelcome();
  const { language } = useLanguage();
  const { isPWAInstalled, platform, canInstall, installPWA } = usePWA();
  const { triggerAfterMBI } = useOnboarding();
  
  // AEAE Integration
  const { 
    quickAnalyze, 
    resetStepTimer, 
    messageVariant, 
    animationSpeed,
    getAnimationDurations,
    uiTone
  } = useAEAE();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [currentVariant, setCurrentVariant] = useState('standard');

  // Detect system theme
  const [systemTheme, setSystemTheme] = useState('dark');
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setSystemTheme(savedTheme);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setSystemTheme(prefersDark ? 'dark' : 'light');
    }
  }, []);

  // Get current platform for tips
  const currentPlatform = useMemo(() => {
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
    return 'desktop';
  }, [platform]);

  // Get animation durations based on emotional state
  const animDurations = useMemo(() => getAnimationDurations(), [getAnimationDurations]);

  // AEAE: Analyze emotional state on step change
  useEffect(() => {
    const analyzeForStep = async () => {
      const stepContexts = ['greeting', 'capabilities', 'tips'];
      const result = await quickAnalyze(stepContexts[currentStep]);
      
      if (result && result.messageVariant) {
        setCurrentVariant(result.messageVariant);
        // Sync with WelcomeContext
        if (result.uiTone === 'calm' || result.uiTone === 'empathetic') {
          setWelcomeTone('calm');
        } else if (result.uiTone === 'energetic') {
          setWelcomeTone('energetic');
        } else {
          setWelcomeTone('neutral');
        }
      }
    };

    resetStepTimer();
    analyzeForStep();
  }, [currentStep, quickAnalyze, resetStepTimer, setWelcomeTone]);

  // Get localized content based on emotional variant
  const messages = useMemo(() => {
    const variant = currentVariant || 'standard';
    return toneMessages[variant]?.[language] || toneMessages.standard[language] || toneMessages.standard.es;
  }, [currentVariant, language]);

  const caps = useMemo(() => {
    const variant = currentVariant || 'standard';
    const langCaps = capabilities[language] || capabilities.es;
    return langCaps[variant] || langCaps.standard;
  }, [currentVariant, language]);

  const tips = useMemo(() => {
    const variant = currentVariant || 'standard';
    const platformData = platformTips[currentPlatform]?.[language] || platformTips.desktop[language] || platformTips.desktop.es;
    return platformData[variant] || platformData.standard;
  }, [currentVariant, language, currentPlatform]);

  const isMobile = currentPlatform !== 'desktop';

  // Animation duration based on AEAE
  const transitionDuration = animDurations.transition;
  const fadeInDuration = animDurations.fadeIn;

  // Handle complete MBI flow
  const handleComplete = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      completeWelcome();
      // Trigger onboarding tour after MBI completes
      triggerAfterMBI();
    }, fadeInDuration);
  }, [completeWelcome, triggerAfterMBI, fadeInDuration]);

  // Handle step navigation with AEAE-aware timing
  const nextStep = useCallback(() => {
    if (currentStep < 2) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, transitionDuration);
    } else {
      handleComplete();
    }
  }, [currentStep, transitionDuration, handleComplete]);

  const handleSkip = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      completeWelcome();
      // Also trigger onboarding after skipping MBI
      triggerAfterMBI();
    }, fadeInDuration);
  }, [completeWelcome, triggerAfterMBI, fadeInDuration]);

  const handleInstallPWA = async () => {
    if (canInstall) {
      await installPWA();
    }
  };

  // Only show on root path, not on login/register/other pages
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const allowedPaths = ['/', '/index.html'];
  const isAllowedPath = allowedPaths.includes(currentPath) || currentPath === '';
  
  if (!showWelcome || !isAllowedPath) return null;

  // Dynamic animation classes based on AEAE state
  const getTransitionClass = () => {
    switch (animationSpeed) {
      case 'slow': return 'duration-500 ease-out';
      case 'fast': return 'duration-150 ease-in-out';
      default: return 'duration-300 ease-out';
    }
  };

  // Step content
  const steps = [
    // Step 0: Greeting
    {
      content: (
        <div className="text-center space-y-6">
          {/* Animated Logo */}
          <div className="relative mx-auto w-24 h-24 mb-8">
            <div 
              className={`absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur-xl opacity-50 ${
                animationSpeed === 'slow' ? 'animate-pulse' : 
                animationSpeed === 'fast' ? 'animate-ping' : 'animate-pulse'
              }`} 
              style={{ animationDuration: `${animDurations.pulse}ms` }}
            />
            <div className="relative w-24 h-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl flex items-center justify-center shadow-2xl">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white">
            {messages.greeting}
          </h1>
          <p className="text-lg text-zinc-400">
            {messages.subtitle}
          </p>
          <p className="text-zinc-500 max-w-md mx-auto">
            {messages.description}
          </p>
        </div>
      )
    },
    // Step 1: Capabilities
    {
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            {language === 'es' ? 'Que puedo hacer por ti?' : 'What can I do for you?'}
          </h2>
          
          <div className="grid gap-4">
            {caps.map((cap, index) => {
              const Icon = cap.icon;
              return (
                <div 
                  key={`cap-${cap.title}`}
                  className={`flex items-center gap-4 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50 hover:border-indigo-500/50 transition-all ${getTransitionClass()}`}
                  style={{ animationDelay: `${index * (animationSpeed === 'fast' ? 50 : animationSpeed === 'slow' ? 150 : 100)}ms` }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{cap.title}</h3>
                    <p className="text-sm text-zinc-400">{cap.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )
    },
    // Step 2: Platform Tips + Install CTA
    {
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            {language === 'es' ? 'Tips para ti' : 'Tips for you'}
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            {currentPlatform === 'desktop' ? (
              <Monitor className="w-5 h-5 text-indigo-400" />
            ) : (
              <Smartphone className="w-5 h-5 text-indigo-400" />
            )}
            <span className="text-sm text-zinc-400">
              {currentPlatform === 'ios' ? 'iPhone/iPad' : 
               currentPlatform === 'android' ? 'Android' : 'Desktop'}
            </span>
          </div>
          
          <div className="space-y-3">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div 
                  key={`tip-${tip.text}`}
                  className={`flex items-center gap-3 p-3 bg-zinc-800/30 rounded-lg transition-all ${getTransitionClass()}`}
                >
                  <Icon className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                  <p className="text-sm text-zinc-300">{tip.text}</p>
                </div>
              );
            })}
          </div>

          {/* Install CTA for non-installed PWA */}
          {!isPWAInstalled && canInstall && (
            <div className={`mt-6 p-4 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-xl border border-indigo-500/30 transition-all ${getTransitionClass()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-indigo-400" />
                  <span className="text-sm text-white">
                    {language === 'es' ? 'Instala la app para mejor experiencia' : 'Install app for better experience'}
                  </span>
                </div>
                <button
                  onClick={handleInstallPWA}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition-colors"
                  data-testid="welcome-install-btn"
                >
                  {language === 'es' ? 'Instalar' : 'Install'}
                </button>
              </div>
            </div>
          )}
        </div>
      )
    }
  ];

  return createPortal(
    <div 
      className={`
        fixed inset-0 z-[99998] flex items-center justify-center
        transition-all ${getTransitionClass()}
        ${isExiting ? 'opacity-0' : 'opacity-100'}
      `}
      data-testid="welcome-flow"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-zinc-950/95 backdrop-blur-xl" />
      
      {/* Background effects - animated based on AEAE */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className={`absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl ${
            animationSpeed === 'slow' ? 'animate-pulse' : 'animate-pulse'
          }`}
          style={{ animationDuration: `${animDurations.pulse}ms` }}
        />
        <div 
          className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl ${
            animationSpeed === 'slow' ? 'animate-pulse' : 'animate-pulse'
          }`}
          style={{ animationDuration: `${animDurations.pulse}ms`, animationDelay: '1000ms' }}
        />
      </div>

      {/* Content */}
      <div className={`
        relative w-full max-w-md mx-4 
        ${isMobile ? 'h-full max-h-[90vh] flex flex-col' : ''}
      `}>
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className={`absolute top-4 right-4 z-10 p-2 text-zinc-500 hover:text-white transition-colors ${getTransitionClass()}`}
          data-testid="welcome-skip-btn"
          aria-label="Skip"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Card */}
        <div className={`
          bg-zinc-900/80 backdrop-blur-lg border border-zinc-800 rounded-2xl
          ${isMobile ? 'flex-1 flex flex-col overflow-hidden' : ''}
          shadow-2xl
        `}>
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 pt-6 pb-4">
            {[0, 1, 2].map((step) => (
              <div 
                key={step}
                className={`
                  h-1.5 rounded-full transition-all ${getTransitionClass()}
                  ${currentStep === step ? 'w-8 bg-indigo-500' : 'w-2 bg-zinc-700'}
                `}
              />
            ))}
          </div>

          {/* Step content */}
          <div className={`
            px-6 pb-6 
            ${isMobile ? 'flex-1 overflow-y-auto' : ''}
            ${isAnimating ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'}
            transition-all ${getTransitionClass()}
          `}>
            {steps[currentStep].content}
          </div>

          {/* Navigation */}
          <div className="px-6 pb-6">
            <button
              onClick={nextStep}
              className={`
                w-full py-3.5 px-6
                bg-gradient-to-r from-indigo-600 to-purple-600
                hover:from-indigo-500 hover:to-purple-500
                text-white font-medium rounded-xl
                flex items-center justify-center gap-2
                transition-all ${getTransitionClass()}
                shadow-lg shadow-indigo-500/25
                active:scale-[0.98]
              `}
              data-testid="welcome-next-btn"
            >
              <span>
                {currentStep === 2 ? messages.cta : (language === 'es' ? 'Siguiente' : 'Next')}
              </span>
              {currentStep < 2 ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ArrowRight className="w-5 h-5" />
              )}
            </button>

            {/* Footer message */}
            <p className="mt-4 text-center text-xs text-zinc-600">
              {language === 'es' 
                ? 'Hecho con ' 
                : 'Made with '}
              <Heart className="w-3 h-3 inline text-red-500" />
              {language === 'es' ? ' por DamyxIA' : ' by DamyxIA'}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WelcomeFlow;
